import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import Contract from 'models/sales/contract/Model.Contract';
import {
  canCreateClaimForContractStatus,
  isClaimContractExpired,
  isClaimContractExpiredWindowExceeded,
} from 'module/claims/helper/Claim.ContractEligibility';
import { useEffect, useMemo, useState } from 'react';
import { usePublicCreateClaimProvider } from '../Hooks';
import { usePublicClaimPageRouter } from '../Index';
import Icon from 'assets/components/icon/Icon';

type ContractSearchForm = {
  contractNumber: string;
  lastName: string;
  vin: string;
};

export default function PublicCreateClaimContractSearch() {
  const { lang, translate } = getLocales();
  const pageRouter = usePublicClaimPageRouter();
  const publicCreateClaim = usePublicCreateClaimProvider();
  const [confirmationContract, setConfirmationContract] = useState<Contract>(undefined);

  const contractListPublic = useApi({
    action: Contract.listPublic,
    wait: true,
  });

  const searchForm = useForm<ContractSearchForm>({
    default: {
      vin: pageRouter.query.vin,
      lastName: pageRouter.query.lastName,
      contractNumber: pageRouter.query.contractNumber,
    },
    validation: (data, errors) => {
      if (!data.vin) errors.vin = lang.required;
      else if (data.vin.length < 8) errors.vin = lang.vinMustBeAtLeast8CharsLong;
      else if (!data.contractNumber && !data.lastName) {
        errors.lastName = lang.required;
        errors.contractNumber = lang.required;
      }
    },
    onSubmit: contractListPublic.execute,
  });

  function toggleContract(contract: Contract, forceSelection?: boolean) {
    if (publicCreateClaim.form?.data?.claim?.contract?.id === contract?.id && !forceSelection) {
      setConfirmationContract(undefined);
      publicCreateClaim.form.reload();
    } else if (isClaimContractExpired(contract)) {
      setConfirmationContract(contract);
    } else {
      publicCreateClaim.form.merge({ claim: { contract } });
    }
  }

  useEffect(() => {
    if (contractListPublic.payload?.data?.length === 1 && !isContractDisabled(contractListPublic.payload.data[0])) {
      toggleContract(contractListPublic.payload.data[0], true);
    }
  }, [contractListPublic.payload?.data]);

  function isContractDisabled(contract: Contract) {
    const hasAllowedStatus = canCreateClaimForContractStatus(contract);
    const isExpiredWindowExceeded = isClaimContractExpiredWindowExceeded(contract);

    return !hasAllowedStatus || isExpiredWindowExceeded;
  }

  const contractDataForDisplay = useMemo(
    () =>
      (contractListPublic.payload?.data ?? publicCreateClaim.form.data?.claim?.contract)
        ? (contractListPublic.payload?.data ?? [new Contract(publicCreateClaim.form.data?.claim?.contract)])
        : [],
    [contractListPublic.payload?.data, publicCreateClaim.form.data?.claim?.contract]
  );

  return (
    <>
      {confirmationContract && (
        <Confirm
          data={confirmationContract}
          onConfirm={(contract) => {
            publicCreateClaim.form.merge({ claim: { contract } });
            setConfirmationContract(undefined);
          }}
          onClose={() => {
            publicCreateClaim.form.reload();
            setConfirmationContract(undefined);
          }}
          actionName={lang.expiredClaimCreationWarning}
          itemName={confirmationContract.displayInfo.title}
          confirmationType="nextButton"
          subcomponents={ThemeConfirmDefault}
        />
      )}
      <h2>{lang.publicSearchContractTitle}</h2>
      <p>{lang.publicSearchContractParagraph}</p>

      <FormWrapper style={{ padding: '0 !important' }}>
        <InputWrapper>
          <TextInput
            label={lang.vin}
            value={searchForm.data.vin}
            onChange={searchForm.update}
            name="vin"
            error={searchForm.errors.vin}
          />
          <TextInput
            label={lang.contractNumber}
            name="contractNumber"
            error={searchForm.errors.contractNumber}
            value={searchForm.data.contractNumber}
            onChange={searchForm.update}
          />
          <TextInput
            label={lang.lastName}
            name="lastName"
            error={searchForm.errors.lastName}
            value={searchForm.data.lastName}
            onChange={searchForm.update}
          />
          <Button
            class={ThemeButtonCircle}
            onClick={() => contractListPublic.execute(searchForm.data as Api.Core.Contract.ListPublic.IRequest)}
            isLoading={contractListPublic.isExecuting}
            disabled={searchForm.hasErrors}
            active={!searchForm.hasErrors}
            style={{ flexShrink: 0 }}
          >
            {lang.search}
          </Button>
        </InputWrapper>
      </FormWrapper>

      <table className={ThemeTableSimple}>
        <thead>
          <tr>
            <th>{lang.contractNumber}</th>
            <th>{lang.status}</th>
            <th>{lang.clientName}</th>
            <th>{lang.vin}</th>
            <th>{lang.expiryDate}</th>
          </tr>
        </thead>
        <tbody>
          {!contractDataForDisplay?.length && (
            <tr>
              <td colSpan={7}>
                <Icon class="fas-inbox" style={{ fontSize: Theme.FontSize.XXL }} />
                <br />
                {lang.noInfo}
              </td>
            </tr>
          )}
          <DataMap
            data={contractDataForDisplay}
            render={({ data: contract }) => {
              const status = contract.statusCode;
              const isPaidActive = status === 'PAID_ACTIVE';
              const isDisabled = isContractDisabled(contract);
              return (
                <tr
                  key={contract.id}
                  onClick={isDisabled ? undefined : () => toggleContract(contract)}
                  data-disabled={isDisabled ? true : undefined}
                  data-active={publicCreateClaim?.form?.data?.claim?.contract?.id === contract.id ? true : undefined}
                  data-clickable={!isDisabled ? true : undefined}
                >
                  <td>
                    <strong>{contract.appNumber}</strong>
                  </td>
                  <td>
                    <Box
                      class={ThemeTagRounded}
                      style={{
                        fontWeight: 'bold',
                        backgroundColor: isPaidActive
                          ? Theme.Color.successLight
                          : isDisabled
                            ? Theme.Color.errorLight
                            : Theme.Color.warningLight,
                        color: isPaidActive
                          ? Theme.Color.success
                          : isDisabled
                            ? Theme.Color.error
                            : Theme.Color.warning,
                      }}
                    >
                      {isPaidActive ? (
                        <Icon class="fas-check-circle" />
                      ) : isDisabled ? (
                        <Icon class="fas-circle" />
                      ) : (
                        <Icon class="fas-exclamation-triangle" />
                      )}
                      &nbsp;
                      {translate(contract.statusCode)}
                    </Box>
                  </td>
                  <td>{contract.client?.displayInfo?.title}</td>
                  <td>{contract.vehicle?.vin}</td>
                  <td>
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {localizeDate(contract.expiryDate, defaultDateFormat)}
                    </span>
                  </td>
                </tr>
              );
            }}
          />
        </tbody>
      </table>
    </>
  );
}
